// Utilities for working with historical person `personJSON` payloads.
//
// The upstream famous/historical person service can return the "why famous"
// value under a mis-cased duplicate key (e.g. `ReasonWhyFamous: null` sitting
// alongside `reasonWhyFamous: "..."`). Because those are distinct keys in
// JavaScript, both survive `JSON.parse`, the mis-cased one masks the real
// value in some consumers, and — worse — the edit dialog spreads the parsed
// object straight back into the saved payload, persisting the stray null so it
// can later clobber the real value. Normalizing on parse coalesces the value
// into the canonical `reasonWhyFamous` key and strips every mis-cased variant
// so it never round-trips back to the service.

const CANONICAL_REASON_KEY = 'reasonWhyFamous'

function hasValue(value) {
  return value !== undefined && value !== null && value !== ''
}

/**
 * Coalesce any case-variant of `reasonWhyFamous` into the canonical key and
 * drop the mis-cased duplicates. Returns a new object; leaves objects without
 * duplicate keys untouched (aside from being shallow-copied).
 */
export function normalizePersonData(personData) {
  if (!personData || typeof personData !== 'object') {
    return personData
  }

  const normalized = { ...personData }
  const variantKeys = Object.keys(normalized).filter(
    (key) => key !== CANONICAL_REASON_KEY && key.toLowerCase() === CANONICAL_REASON_KEY.toLowerCase()
  )

  if (variantKeys.length === 0) {
    return normalized
  }

  if (!hasValue(normalized[CANONICAL_REASON_KEY])) {
    const variantWithValue = variantKeys.find((key) => hasValue(normalized[key]))
    if (variantWithValue) {
      normalized[CANONICAL_REASON_KEY] = normalized[variantWithValue]
    }
  }

  variantKeys.forEach((key) => delete normalized[key])

  return normalized
}

/**
 * Parse a `personJSON` string and normalize the mis-cased "why famous" key.
 */
export function parseHistoricalPersonJSON(personJSON) {
  return normalizePersonData(JSON.parse(personJSON))
}
